import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Calendar, Clock, X, ImageOff, Settings, Shield, Globe, Zap, Search, Newspaper } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Image } from "@/components/ui/image";
import Navbar from "@/components/tchatix/Navbar";
import Footer from "@/components/tchatix/Footer";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { useTranslation } from "react-i18next";

const CALENDLY_URL = "https://calendly.com/tchatixdemo/audit?background_color=001d31&text_color=ffffff&primary_color=0985d8";

// Ordre d'affichage de la presse (les contenus traduits viennent de blog.presse.<key>)
const PRESSE_KEYS = ["nouvelEco", "bfm", "france2030", "ovh", "varMatin", "sixFours", "lesPepitesTech"];

export default function Blog() {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [visibleCount, setVisibleCount] = useState(9);

  const PRESSE = PRESSE_KEYS.map((key) => ({
    key,
    outlet: t(`blog.presse.${key}.outlet`),
    outletLogo: t(`blog.presse.${key}.outletLogo`),
    title: t(`blog.presse.${key}.title`),
    excerpt: t(`blog.presse.${key}.excerpt`),
    type: t(`blog.presse.${key}.type`),
    date: t(`blog.presse.${key}.date`),
    href: t(`blog.presse.${key}.href`, { defaultValue: null })
  }));

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return "";
    }
  };

  // Catégories disponibles (extraites des articles)
  const categories = [...new Set(articles.map((a) => a.category).filter(Boolean))];

  // Articles filtrés
  const filteredArticles = articles.filter((a) => {
    const matchSearch = !searchQuery ||
    a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = !activeCategory || a.category === activeCategory;
    return matchSearch && matchCategory;
  });
  const visibleArticles = filteredArticles.slice(0, visibleCount);
  const hasMore = filteredArticles.length > visibleCount;

  // Chargement des articles
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await base44.entities.BlogArticle.list("-created_date", 1000);
        if (!cancelled) setArticles(Array.isArray(list) ? list : list?.items || []);
      } catch (e) {
        if (!cancelled) setArticles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {cancelled = true;};
  }, []);

  // Réinitialiser visibleCount quand la recherche ou la catégorie change
  useEffect(() => {
    setVisibleCount(9);
  }, [searchQuery, activeCategory]);

  return (
    <div className="relative min-h-[100svh] text-[#001D31] overflow-x-clip bg-[#F4F6F8]">
      <style>{`
        @media (max-width: 767px) {
          .tx-glass, .tx-glass-strong {
            -webkit-backdrop-filter: none !important;
            backdrop-filter: none !important;
            background: rgba(255,255,255,0.92) !important;
          }
        }
        html, body, #root { overflow-x: clip; }
      `}</style>

      <Seo
        title={t("blog.metaTitle")}
        description={t("blog.metaDesc")}
        path="/blog"
        jsonLd={breadcrumbLd([
        { name: t("blog.breadcrumbHome"), path: "/" },
        { name: t("blog.breadcrumbBlog"), path: "/blog" }]
        )} />
      
      <Navbar />

      {/* ── Hero — sobre, aligné à gauche comme base44.com/blog ── */}
      <section className="pt-28 md:pt-32 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs text-[#8896B0] mb-4 flex items-center gap-1.5">
            <Link to="/" className="hover:text-[#0985D8]">{t("blog.breadcrumbHome")}</Link>
            <span>/</span>
            <span className="text-[#001D31] font-medium">{t("blog.breadcrumbBlog")}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight mb-5 [font-family:'Playfair_Display',_serif] text-[#001D31]">
            {t("blog.heroTitle1")} <span className="tx-gradient-text">{t("blog.heroTitle2")}</span>
          </h1>
          <p className="text-[#5A6675] max-w-2xl text-base md:text-lg [font-family:'Poppins',_sans-serif] leading-relaxed">
            {t("blog.heroDesc")}
          </p>

          {/* Preuve live */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-xs text-[#8896B0]">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3CBB7A]" />
              <strong className="text-[#001D31]">{articles.length}</strong> {t("blog.articlesPublished")}
            </span>
            <span className="flex items-center gap-2">
              {t("blog.updatedContinuous")} <Clock className="w-3 h-3" />
            </span>
          </div>
        </div>
      </section>

      {/* ── Layout deux colonnes : sidebar gauche + flux articles ── */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">

          {/* ════════ SIDEBAR GAUCHE ════════ */}
          <aside className="lg:sticky lg:top-24 self-start space-y-8">

            {/* Ruban — Tchatix dans les médias */}
            <div>
              <p className="text-[11px] font-bold tracking-[0.14em] text-[#001D31] uppercase mb-3 flex items-center gap-2">
                <Newspaper className="w-3.5 h-3.5 text-[#0985D8]" />
                {t("blog.presseBadge")}
              </p>
              <div className="lg:hidden">
                <PresseAccordion items={PRESSE} />
              </div>
              <div className="hidden lg:block bg-white rounded-2xl border border-[#001D31]/8 divide-y divide-[#001D31]/5">
                {PRESSE.map((p, i) =>
                <a
                  key={i}
                  href={p.href || "#"}
                  target={p.href ? "_blank" : undefined}
                  rel={p.href ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-[#F0F8FF] transition-colors group">
                    <div className="w-8 h-8 rounded-lg bg-white border border-[#001D31]/8 p-1 shrink-0 flex items-center justify-center">
                      <Image src={p.outletLogo} alt={p.outlet} fittingType="fit" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-[#001D31] truncate group-hover:text-[#0985D8] transition-colors">{p.outlet}</p>
                      <p className="text-[10px] text-[#8896B0] truncate">{p.type}</p>
                    </div>
                    {p.href && <ExternalLink className="w-3 h-3 text-[#8896B0] shrink-0" />}
                  </a>
                )}
              </div>
            </div>

            {/* Sous-catégories — Agent SEO (desktop only) */}
            <div className="hidden lg:block">
              <p className="text-[11px] font-bold tracking-[0.14em] text-[#001D31] uppercase mb-3">
                {t("blog.seoBadge")}
              </p>
              <nav className="flex flex-col gap-0">
                <CategoryLink
                  active={!activeCategory}
                  onClick={() => setActiveCategory(null)}
                  label={t("blog.allCategories")}
                  count={articles.length} />
                
                {categories.map((cat) =>
                <CategoryLink
                  key={cat}
                  active={activeCategory === cat}
                  onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                  label={cat}
                  count={articles.filter((a) => a.category === cat).length} />

                )}
              </nav>
            </div>

            {/* Recherche */}
            <div className="hidden lg:block">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("blog.searchPlaceholder")}
                  className="w-full px-4 py-2.5 pl-9 rounded-xl bg-white border border-[#001D31]/10 text-sm text-[#001D31] placeholder-[#8896B0]/60 focus:outline-none focus:border-[#0985D8]/60" />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8896B0]" />
              </div>
            </div>
          </aside>

          {/* ════════ FLUX PRINCIPAL ════════ */}
          <div>
            {/* Recherche mobile */}
            <div className="lg:hidden mb-5">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("blog.searchPlaceholder")}
                  className="w-full px-4 py-2.5 pl-9 rounded-xl bg-white border border-[#001D31]/10 text-sm text-[#001D31] placeholder-[#8896B0]/60 focus:outline-none focus:border-[#0985D8]/60" />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8896B0]" />
              </div>
            </div>

            {/* Compteur */}
            {!loading && articles.length > 0 &&
            <p className="text-xs text-[#8896B0] mb-5">
                {filteredArticles.length} {filteredArticles.length > 1 ? t("blog.articlesWord") : t("blog.articleWord")} {t("blog.on")} {articles.length}
              </p>
            }

            {loading ?
            <div className="grid sm:grid-cols-2 gap-5">
                {[0, 1, 2, 3].map((i) =>
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-[#001D31]/8 animate-pulse">
                    <div className="h-44 bg-[#E8EEF2]" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-[#E8EEF2] rounded w-3/4" />
                      <div className="h-3 bg-[#E8EEF2] rounded w-full" />
                      <div className="h-3 bg-[#E8EEF2] rounded w-2/3" />
                    </div>
                  </div>
              )}
              </div> :
            articles.length === 0 ?
            <div className="text-center max-w-md mx-auto bg-white rounded-2xl p-10 border border-[#001D31]/8">
                <ImageOff className="w-10 h-10 text-[#0985D8] mx-auto mb-4" />
                <p className="text-[#001D31] font-medium mb-2">{t("blog.emptyTitle")}</p>
                <p className="text-sm text-[#8896B0] mb-5">{t("blog.emptyText")}</p>
                <a
                href="/admin/blog"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5A3C] text-white text-sm font-medium hover:bg-[#001D31] transition-colors">
                  <Settings className="w-4 h-4" /> {t("blog.adminSpace")}
                </a>
              </div> :

            <>
                <div className="grid sm:grid-cols-2 gap-5">
                  {visibleArticles.map((a) =>
                <Link
                  key={a.id}
                  to={`/article/${a.slug}`}
                  className="text-left bg-white rounded-2xl overflow-hidden flex flex-col border border-[#001D31]/8 group hover:border-[#0985D8]/30 hover:-translate-y-1 transition-all duration-200">

                      <div className="relative aspect-[16/10] overflow-hidden">
                        {a.cover_image_url ?
                    <Image src={a.cover_image_url} alt={a.title} fittingType="fill" className="w-full h-full transition-transform duration-500 group-hover:scale-105" /> :

                    <div className="w-full h-full bg-gradient-to-br from-[#0985D8]/20 to-[#A78BFA]/10" />
                    }
                        {a.tag &&
                    <span className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full bg-black/55 text-white">
                            {a.tag}
                          </span>
                    }
                      </div>
                      <div className="p-5 flex flex-col gap-2.5 flex-1">
                        <span className="text-[10px] text-[#8896B0] flex items-center gap-2">
                          <Calendar className="w-3 h-3" /> {formatDate(a.created_date)}
                          {a.read_time && <><span className="w-px h-2.5 bg-[#001D31]/15" /><Clock className="w-3 h-3" /> {a.read_time}</>}
                        </span>
                        <h3 className="text-lg font-semibold text-[#001D31] leading-snug group-hover:text-[#0985D8] transition-colors [font-family:'Playfair_Display',_serif]">
                          {a.title}
                        </h3>
                        <p className="text-sm text-[#5A6675] leading-relaxed line-clamp-3 flex-1">{a.excerpt}</p>
                        <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#001D31]/8">
                          {a.category && <span className="text-[10px] uppercase tracking-wider font-medium text-[#0985D8]">{a.category}</span>}
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#0985D8] group-hover:gap-2 transition-all ml-auto">
                            {t("blog.read")} <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                )}
                </div>

                {hasMore &&
              <div className="flex justify-center mt-10">
                    <button
                  onClick={() => setVisibleCount(visibleCount + 9)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#0985D8]/20 text-[#0985D8] text-sm font-medium hover:bg-[#0985D8] hover:text-white transition-colors">
                      {t("blog.loadMore", { count: filteredArticles.length - visibleCount })}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
              }
              </>
            }
          </div>
        </div>
      </section>

      {/* ── CTA Contact — fond bleu Tchatix plein largeur + rayures verticales blanches ── */}
      <section className="relative w-full overflow-hidden">
        {/* Fond bleu Tchatix plein largeur */}
        <div className="absolute inset-0" style={{ backgroundColor: "#0985D8" }} />
        {/* Texture rayée verticale — clin d'œil au logo */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,1) 0px, rgba(255,255,255,1) 10px, transparent 10px, transparent 60px)"
          }} />
        {/* Grain fin pour la matière */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 0.5px, transparent 0.5px)",
            backgroundSize: "3px 3px"
          }} />

        <div className="relative z-10 py-20 md:py-24 px-6 flex flex-col items-center text-center">
          <div className="flex flex-col items-center max-w-lg">
            <h2 className="text-[28px] md:text-[44px] font-semibold leading-[1.15] mb-6 [font-family:'Playfair_Display',_serif] text-white">
              {t("blog.ctaTitle1")}<br />
              <span className="text-white">{t("blog.ctaTitle2")}</span>
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-[12px] font-medium">
                {t("blog.badge247")}
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-[12px] font-medium">
                <Globe className="w-3.5 h-3.5" />
                {t("blog.badgeSeoLocal")}
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-[12px] font-medium">
                <Zap className="w-3.5 h-3.5" />
                {t("blog.badgeTraffic")}
              </div>
            </div>
            <p className="text-sm text-white/80 mb-8 [font-family:'Poppins',_sans-serif]">{t("blog.ctaDesc")}</p>
            <button
              onClick={() => setDemoOpen(true)}
              className="group/btn relative inline-flex items-center gap-3 px-8 py-4 rounded-full overflow-hidden cursor-pointer border-2 border-white">
              <span className="absolute inset-0 bg-white" />
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0985D8]/10 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 ease-out" />
              <Globe className="w-4 h-4 text-[#0985D8] relative z-10" />
              <span className="relative z-10 font-semibold text-base text-[#0985D8]">{t("blog.ctaBtn")}</span>
              <ArrowRight className="w-4 h-4 text-[#0985D8] relative z-10 group-hover/btn:translate-x-1 transition-transform" />
            </button>
            





            
          </div>
        </div>
      </section>

      <Footer />

      {/* ── Démo pop-up (Calendly) ── */}
      <AnimatePresence>
        {demoOpen &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
          onClick={() => setDemoOpen(false)}>
            <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl bg-white rounded-3xl overflow-hidden border border-[#001D31]/10">
              <button
              onClick={() => setDemoOpen(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white hover:bg-[#F0F8FF] border border-[#001D31]/10 flex items-center justify-center text-[#001D31] transition-colors"
              aria-label={t("blog.closeDemo")}>
                <X className="w-5 h-5" />
              </button>
              <iframe src={CALENDLY_URL} className="w-full" style={{ height: "660px", border: "0" }} />
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>
    </div>);

}

// ── Lien de catégorie (style base44 : [ Actif ] avec crochets) ──
function CategoryLink({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
      active ?
      "bg-[#0985D8]/8 text-[#001D31] font-semibold" :
      "text-[#5A6675] hover:bg-[#001D31]/4 hover:text-[#001D31]"}`
      }>
      <span className="flex items-center gap-1.5">
        {active && <span className="text-[#0985D8]">[</span>}
        {label}
        {active && <span className="text-[#0985D8]">]</span>}
      </span>
      {count > 0 && <span className="text-[10px] text-[#8896B0]">{count}</span>}
    </button>);

}

// ── Accordéon Presse (mobile) ──
function PresseAccordion({ items }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const featured = ["bfm", "nouvelEco", "ovh"];
  const featuredItems = items.filter((p) => featured.includes(p.key));

  return (
    <div className="rounded-2xl overflow-hidden bg-white border border-[#001D31]/10">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full px-4 py-3.5 flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-xl bg-[#0985D8]/10 flex items-center justify-center shrink-0">
          <Newspaper className="w-4.5 h-4.5 text-[#0985D8]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-[#001D31] leading-tight">{t("blog.presseBadge")}</p>
          <p className="text-[11px] text-[#8896B0]">{items.length} {t("blog.presseTitle")}</p>
        </div>
        <div className="flex items-center -space-x-2">
          {featuredItems.map((p, i) =>
          <div key={i} className="w-8 h-8 rounded-full bg-white border border-[#001D31]/10 p-1.5 flex items-center justify-center overflow-hidden">
              <Image src={p.outletLogo} alt={p.outlet} fittingType="fit" className="w-full h-full object-contain" />
            </div>
          )}
        </div>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? "bg-[#0985D8] text-white" : "bg-[#001D31]/5 text-[#001D31]"}`}>
          <svg className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
        <div className="overflow-hidden min-h-0">
          <div className="px-3 pb-4 pt-3 flex flex-col gap-2.5 border-t border-[#001D31]/5 bg-[#F7FAFD]">
            {items.map((p, i) => {
              const isFeatured = featured.includes(p.key);
              return (
                <a
                  key={i}
                  href={p.href || "#"}
                  target={p.href ? "_blank" : undefined}
                  rel={p.href ? "noopener noreferrer" : undefined}
                  className={`relative overflow-hidden rounded-2xl border flex items-center gap-3 p-3 ${
                  isFeatured ? "bg-[#F0F8FF] border-[#0985D8]/20" : "bg-white border-[#001D31]/[0.07]"}`
                  }>
                  <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${isFeatured ? "bg-[#0985D8]" : "bg-[#0985D8]/30"}`} />
                  <div className="w-11 h-11 rounded-xl bg-white p-1.5 shrink-0 flex items-center justify-center border border-[#001D31]/[0.08]">
                    <Image src={p.outletLogo} alt={p.outlet} fittingType="fit" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[12px] font-semibold text-[#001D31] truncate">{p.outlet}</span>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${isFeatured ? "bg-[#0985D8] text-white" : "bg-[#0985D8]/10 text-[#0985D8]"}`}>{p.type}</span>
                    </div>
                    <p className="text-[11.5px] text-[#001D31]/75 leading-snug line-clamp-2 mb-1.5">{p.title}</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] text-[#8896B0] flex items-center gap-1 shrink-0">
                        <Calendar className="w-2.5 h-2.5" /> {p.date}
                      </span>
                      {p.href ?
                      <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#0985D8] shrink-0">
                          {t("blog.read")} <ExternalLink className="w-2.5 h-2.5" />
                        </span> :

                      <span className="text-[9px] font-semibold text-[#3CBB7A] shrink-0">{t("blog.official")}</span>
                      }
                    </div>
                  </div>
                </a>);

            })}
          </div>
        </div>
      </div>
    </div>);

}